import { useClientTranslations } from "../lib/i18n/client";
import { resolveTreatmentType } from "../types/journey";
import { useJourneyProgress } from "./useJourneyProgress";
import { useUser } from "./useUser";

export interface JourneyOverallProgress {
	/** 0–100, rounded: completed steps ÷ total steps in the user's journey. */
	percent: number;
	completedSteps: number;
	totalSteps: number;
	/** Journey tasks not yet completed (drives the "X more tasks" subtitle). */
	remainingTasks: number;
	totalTasks: number;
	isLoading: boolean;
	/** false when the user has no supported journey (e.g. "Other"/"don't know"). */
	hasJourney: boolean;
}

/**
 * Overall progress through the user's fertility journey, for the home-page ring.
 *
 * Computed on the client from data the app already loads (`useJourneyProgress`):
 * the journey catalog (total steps, FE-only CDN JSON) merged with the server's
 * per-step states. A step counts as complete when the live server state says so,
 * falling back to the CDN-marked status. There is no backend overall-% field —
 * the M011 design keeps journey totals on the frontend, so this is the natural
 * place to compose them. Maps the product's "section" notion to journey steps.
 */
export function useJourneyOverallProgress(): JourneyOverallProgress {
	const { locale } = useClientTranslations();
	const { data: user } = useUser();
	const treatmentType = resolveTreatmentType(user?.details);
	const { journey, stepStates, completedTaskIds, isLoading } =
		useJourneyProgress(treatmentType, { locale });

	const totalSteps = journey?.steps.length ?? 0;
	const completedSteps = journey
		? journey.steps.filter((step) => {
				const live = stepStates.get(step.id);
				// Live server state wins; otherwise trust the CDN-marked status.
				if (live) return live.status === "complete";
				return step.status === "completed";
			}).length
		: 0;
	const percent =
		totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

	// Task-level remaining count for the "X more tasks" subtitle. A task is done
	// when it's in the live completedTaskIds OR already marked complete in the CDN
	// catalog — same rule Journey.tsx uses, so a CDN-complete task isn't counted as
	// remaining (which would inflate the number).
	let totalTasks = 0;
	let completedTasks = 0;
	if (journey) {
		for (const step of journey.steps) {
			for (const task of step.tasks) {
				totalTasks++;
				if (completedTaskIds.has(task.id) || task.status === "completed") {
					completedTasks++;
				}
			}
		}
	}
	const remainingTasks = Math.max(0, totalTasks - completedTasks);

	return {
		percent,
		completedSteps,
		totalSteps,
		remainingTasks,
		totalTasks,
		isLoading,
		hasJourney: totalSteps > 0,
	};
}
